<script lang="ts" setup>
import AUApi from "@/api";
import { ref, onMounted, computed } from "vue";
import { getKZColumn, getValue } from "@/DataHelper";
import { Globals } from "@/Globals";
import { begriffBezeichnung, globalsBezeichnung } from "@/local";
import { findKennungSperre } from "@/SperreHelper";
import {
  AufgabeStatus,
  AufgabeStatusContainer,
  AufgabeStatusZuordnung,
  AufgabeStatusZuordnungAufgabe,
  AufgabeStatusFrame,
  callQuickviewSelectionCallBack,
} from "./ChartTypesAndFunctions";
declare type AufgabeItemMode = "Nebeneinander" | "QuickView" | "Tab";
import CommonBaseFilter from "@/components/common//BaseFilter.vue";
import DashboardChartsZuordnungenMenu from "@/components/dashboard/Charts/ZuordnungenMenu.vue";
import DashboardAufgabeBoardItemCard from "@/components/dashboard/AufgabeBoardItemCard.vue";
import { goBack } from "@/NavigationHelper";

let props = defineProps<{
  aufgabe: ALLAufgabe;
  orientation?: ItemOrientation;
  aufgabenDefinition: QueryResponse;
  showTitle?: boolean;
  persoenlich?: boolean;
  aufgabeStatus?: AufgabeStatus[];
  size?: number;
  aic?: number;
  zeitbereich: APIZeitbereich;
}>();
const emit = defineEmits<{
  dataLoaded: AufgabeStatus[];
  back: [];
  dashboardItemTitle: [item?: any];
  hideAufgabeTitle: [item?: any];
}>();
let activeTabIndex = ref<number>(0);

// daten bevor sie gefiltert wurden
const filterData = ref<QueryResponse | null>(null);
// fertig gefilterte Daten...
let filteredData: QueryResponse | null = null;

let showLoading = ref<boolean>(false);
let waitingForQVAics = ref<boolean>(false);
const cardKey = ref(0);

// gekapselt damit es nur einen Modus geben KANN (nicht mixed mist)
let Mode: AufgabeItemMode = "Nebeneinander";
let QuickViewMode = ref(false);
let TabMode = ref(false);

let QvAICs = ref<number[]>([]);
let statusListe = ref<AufgabeStatusContainer[]>([]);

let QVZuordnungen = ref<AufgabeStatusZuordnung[]>([]);
let nextAbschluss = ref<string>("");
const vector = ref<number[]>();

let page = ref<number>(0);
let pageSize: number = 10;

onMounted(async () => {
  showLoading.value = true;
  if (props.aufgabe) {
    let response = await AUApi.Current.getStatusAufgaben(props.aufgabe.aic);
    const text = begriffBezeichnung(
      "Web_keineDaten_gefunden",
      "keine Daten gefunden",
    );
    if (response.status > 210) {
      alert(text);
    } else if (response.data.length === 0) {
      alert(text);
    }
    QuickViewMode.value = Mode === "QuickView";
    let status = response.data as AufgabeStatus[];
    await refreshAufgabeStatus(status);
  } else if (props.aufgabeStatus) {
    await refreshAufgabeStatus(props.aufgabeStatus);
  }
});
// watch("aufgabe");
// watch("aufgabeStatus");
async function refreshAufgabeStatus(status: AufgabeStatus[]) {
  let qv = status.find((s) => s.darstellungKennung === "QuickView");
  // qv rauslösen... und in QuickViewMode schalten
  if (qv) {
    waitingForQVAics.value = true;
    QVZuordnungen.value = qv.zuordnung ?? [];
    Mode = "QuickView";
    QuickViewMode.value = true;
    emit("hideAufgabeTitle"); // titel verstecken
  } else if (props.aufgabe.tab) {
    Mode = "Tab";
    TabMode.value = true;
  }
  statusListe.value = status.map((s) => {
    let as: AufgabeStatusContainer = {
      status: s,
      zeitbereich: props.zeitbereich,
      orientation: props.orientation ?? "vertical",
      icon: s.icon ?? "",
      Nr: s.Nr ?? 0,
      aic: s.aic ?? 0,
      bezeichnung: s.bezeichnung ?? "",
      darstellungKennung: s.darstellungKennung,
      filterKennung: s.filterKennung ?? "",
      hsAic: s.hsAic ?? 0,
      params: {
        zeitbereich: props.zeitbereich,
        aufgabenDefinition: props.aufgabenDefinition,
        query: s.filterKennung!,
        hsDefinition: s.hsAic,
        orientation: props.orientation,
        aic: props.aic,
        zuordnungen: s.zuordnung?.filter((z) => z.gruppe !== "Aufgabe"),
        zusatzInfo: s.zuordnung?.find(
          (z) =>
            z.gruppe === "Aufgabe" ||
            z.gruppe === "Frame" ||
            z.gruppe === "Hauptschirm",
        ) as AufgabeStatusZuordnungAufgabe,
        aufgabe: props.aufgabe,
      },
    };
    setChartType(as);

    as.params.aicStamm = as.status.stamm;

    if (qv) {
      as.params.size = 200;
    }
    if (
      as?.status?.darstellungKennung !== "Liste" &&
      as?.status?.darstellungKennung !== "KPI"
    ) {
      as.titel = s.bezeichnung;
      as.params.abwZeitbereich = s.zeitArt;
    } else if (as?.status?.darstellungKennung === "KPI") {
      as.params.title = s.bezeichnung;
      as.params.icon = s.bild;
      as.params.abwZeitbereich = s.zeitArt;
      as.params.tooltip = s.tooltip;
    }
    return as;
  });
  if (props.aufgabe?.abschlussTyp) {
    getSperrDate(props.aufgabe.abschlussTyp);
  }
  emit("dataLoaded", ...status);
  showLoading.value = false;
}

function selectChangeAll(val: unknown) {
  callQuickviewSelectionCallBack(val as boolean);
}

function getSperrDate(begriff: any) {
  let text = globalsBezeichnung(
    Globals.Begriff,
    "Web_gueltig_Ab",
    "gültig ab:",
  );
  if (begriff) {
    let sperre = findKennungSperre(begriff);
    if (sperre && sperre.sperre.next) {
      let sperrDatum = new Date(sperre.sperre.next)
        .addDays(-1)
        .toLocaleDateString();
      let gueltigAb = "";
      if (sperre.sperre.ab) {
        let gueltig = new Date(sperre.sperre.ab).toLocaleDateString();
        if (gueltig) {
          gueltigAb = " " + text + " " + gueltig;
        }
      }
      nextAbschluss.value =
        sperre.sperre.bezeichnung + ": " + sperrDatum + gueltigAb;
    }
  }
  return null;
}

function setChartType(as: AufgabeStatusContainer) {
  switch (as.status.darstellungKennung) {
    case "Balken_Chart":
      as.params.chartType = "Bar";
      as.params.chartOrientation = "vertical";
      break;
    case "Balken_Horizontal":
      as.params.chartType = "Bar";
      as.params.chartOrientation = "horizontal";
      break;
    case "BalkenStackedMinMax":
      as.params.chartType = "BalkenStackedMinMax"; //"BalkenStackedMinMax"
      as.params.chartOrientation = "horizontal";
      break;
    case "Chart_Kreis":
      as.params.chartType = "Pie";
      break;
    case "Tabelle":
    case "Liste":
    case "Kalender":
    case "QuickView":
      break;
  }
}
function pages() {
  let count = Math.floor(AllQvAics.length / pageSize);
  let rest = AllQvAics.length % pageSize;
  return count + (rest > 0 ? 1 : 0);
}

function setPage(ix: number) {
  let start = pageSize * ix;
  QvAICs.value = AllQvAics.slice(start, start + pageSize);
  page.value = ix;
}
let AllQvAics: number[] = [];
// Reaktives Gegenstück zu AllQvAics (ungepaginiert) - QvAICs ist auf pageSize (Default 10,
// per props.aufgabe.pagAnzahl konfigurierbar) begrenzt für Liste/Tabelle/Kalender mit
// sichtbarer Seiten-Navigation. Ein Chart (CChart) soll aber immer den gesamten Datensatz
// aggregieren, nicht nur die aktuell angezeigte Seite - dafür bekommt es allQvAicsFull.
let allQvAicsFull = ref<number[]>([]);
function quickViewAicsLoaded(...aics: number[]) {
  pageSize = props.aufgabe.pagAnzahl ?? pageSize;
  AllQvAics = aics;
  allQvAicsFull.value = aics;
  setPage(0);
  waitingForQVAics.value = false;
}

function vectorChanged(...payload: number[]) {
  const vec = Array.isArray(payload[0])
    ? (payload[0] as number[])
    : (payload as number[]);
  vector.value = [...vec];
}

const handleBuildFilter = (...args: unknown[]) => {
  const payload = (args[0] as QueryResponse | null) ?? null;
  filterData.value = payload;
};
function filteredDataChanged(payload: QueryResponse) {
  if (!payload) return;
  filterData.value = payload;
  let aicCol = getKZColumn("modellAic", filterData.value.columns);

  // neue aics rausfiltern
  let aics = filterData.value.data.map((row: QueryDataRow) => {
    let aic = 0;
    if (aicCol) {
      aic = getValue(row, aicCol);
    }
    if (!aic) {
      if (row.aic_Bew_pool) {
        aic = row.aic_Bew_pool;
      } else {
        aic = row.aic_Stamm;
      }
    }
    return aic;
  });

  // neue AicsListe setzen
  if (!aics || aics.length === 0) {
    // Wenn keine AICs gefunden wurden: QvAICs leeren und keine Daten anzeigen
    QvAICs.value = [];
    waitingForQVAics.value = false;
    // auch die Gesamtliste/Paginierung zurücksetzen
    AllQvAics = [];
    allQvAicsFull.value = [];
    page.value = 0;
  } else {
    QvAICs.value = [...aics];
    // AllQvAics (pageSize-Paginierung) bewusst unverändert lassen - für Liste/Tabelle wird
    // bei aktivem Filter direkt der volle gefilterte Satz in QvAICs angezeigt (bestehendes
    // Verhalten). allQvAicsFull ist nur für CChart (siehe quickViewAicsLoaded) relevant.
    allQvAicsFull.value = [...aics];
  }
  cardKey.value++;
}

function getStatusColumns(as: AufgabeStatus, ix: number) {
  if (Mode === "Tab") {
    return "col-12";
  }
  return "col-" + 12 / statusListe.value.length;
}
</script>
<template>
  <v-container class="grey lighten-5 pa-0 ma-0">
    <v-progress-linear
      color="warning"
      indeterminate
      v-if="showLoading"
    ></v-progress-linear>
    <v-toolbar
      density="compact"
      class="dvhBackground"
      v-if="pages() > 1 || QuickViewMode"
    >
      <v-toolbar-title v-if="QuickViewMode" style="color: white">
        {{ aufgabe.bezeichnung }}
      </v-toolbar-title>
      <v-spacer></v-spacer>

      <v-checkbox
        v-if="QuickViewMode && QVZuordnungen && QVZuordnungen.length > 0"
        dark
        style="color: white"
        @update:modelValue="selectChangeAll"
        class="pr-5 pt-5"
        :label="globalsBezeichnung(Globals.Begriff, 'alle', 'alle')"
      ></v-checkbox>

      <DashboardChartsZuordnungenMenu
        v-if="QuickViewMode && QVZuordnungen"
        :zuordnungen="QVZuordnungen"
        :vector="vector"
        :showButtons="true"
        class="inlineBlock"
        :color="'white'"
        :onlyVector="true"
      />

      <CommonBaseFilter
        v-if="filterData"
        :queryData="filterData"
        @filteredDataChanged="filteredDataChanged"
        class="inlineBlock"
      />

      <!-- <v-col
        cols="2"
        v-if="pages() > 1"
        class="pa-0 pt-2 text-center"
        style="color: white"
      >
        <v-btn
          @click="setPage(page - 1)"
          :disabled="page === 0"
          icon="mdi-chevron-left"
        >
        </v-btn>
        {{ page + 1 }}/{{ pages() }}
        <v-btn
          @click="setPage(page + 1)"
          :disabled="page === pages() - 1"
          icon="mdi-chevron-right"
        >
        </v-btn>
      </v-col> -->
    </v-toolbar>
    <v-row v-if="TabMode" style="padding: 10px; margin: 0">
      <v-tabs v-model="activeTabIndex">
        <v-tab v-for="(as, ix) in statusListe" :key="'tab-' + ix">{{
          as.status.bezeichnung
        }}</v-tab>
      </v-tabs>
    </v-row>
    <v-row justify="center" no-gutters>
      <v-col
        :cols="getStatusColumns(as, ix)"
        v-for="(as, ix) in statusListe"
        :key="'i' + ix"
        no-gutters
      >
        <v-scroll-x-transition>
          <DashboardAufgabeBoardItemCard
            :key="cardKey"
            :as="as"
            v-if="
              (!TabMode || activeTabIndex === ix) &&
              (!QuickViewMode ||
                waitingForQVAics ||
                (QvAICs && QvAICs.length > 0) ||
                as.params.chartType)
            "
            :params="as.params"
            :QvAICs="QvAICs"
            :AllQvAics="allQvAicsFull"
            :waitingForQVAics="waitingForQVAics"
            :QuickViewMode="QuickViewMode"
            @aicsLoaded="quickViewAicsLoaded"
            @vectorChanged="vectorChanged"
            @dashboardItemTitle="$emit('dashboardItemTitle', $event)"
            @hideAufgabeTitle="$emit('hideAufgabeTitle', $event)"
            @back="goBack()"
            @buildFilter="handleBuildFilter"
            :persoenlich="persoenlich"
          />
        </v-scroll-x-transition>
      </v-col>
    </v-row>
    <v-row v-if="nextAbschluss">
      <v-col>{{ nextAbschluss }}</v-col>
    </v-row>
  </v-container>
</template>
<style lang="css" scoped>
.boardTitle {
  padding: 0;
  margin: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  display: inline-block;
}

.checkBoxAll > .v-input--selection-controls.v-inputsa {
  padding-top: 20px !important;
}
</style>
